import { Box, Flex, HStack, VStack } from '@chakra-ui/react';
import React from 'react';
import moment from 'moment';
import { lensProp, mergeDeepLeft, set } from 'ramda';
import { useTokens } from '../../../../context/TokenProvider';
import { localise } from '../../../../services/lang/lang';
import PageHeader from '../../../modules/PageHeader';
import PageContent from '../../../modules/PageContent/PageContent';
import { CLOSED } from '../../../../constants/statuses';
import ContactsWrapper from '../../../modules/Contacts/ContactsWrapper';
import { InspectionDetailProvider } from './InspectionDetailProvider';
import InspectionDetailDwellingDetailsAccordion from './Accordions/InspectionDetailDwellingDetailsAccordion';
import InspectionDetailHeader from './Header/InspectionDetailHeader';
import { RenderIf } from '../../../modules/RenderIf/RenderIf';
import InspectionDetailCompletedAlert from './Alerts/InspectionDetailCompletedAlert';
import usePolling from '../../../../hooks/usePolling';
import { unwrapModel } from '../../../../services/api/unwrapModel';
import { getOneProperty } from '../../../../services/api/generated/client';
import InspectionDetailDataPopulationAlert from './Alerts/InspectionDetailDataPopulationAlert';
import TenantReportsCallToAction from './TenantReports/TenantReportsCallToAction';
import { useFeatureFlags } from '../../../../context/FeatureFlagProvider';
import InspectionDetailTabs from '../../../modules/InspectionDetailTabs/InspectionDetailTabs';
import InspectionDetailPropertyOverviewAccordion from './Accordions/InspectionDetailPropertyOverviewAccordion';
const POLL_MAX_WAIT_TIME_MS = 10000;
const POLL_INTERVAL = 2500;
const POLL_MAX_ATTEMPTS = 3;
const shouldEnablePolling = (property, isShare) => {
    if (isShare || property.data_retrieval_attempted_at) {
        return false;
    }
    const propertyCreatedAt = moment(property.created_at);
    const hasExceededMaxWait = propertyCreatedAt
        .add(POLL_MAX_WAIT_TIME_MS, 'milliseconds')
        .isBefore(moment());
    return !hasExceededMaxWait;
};
const InspectionDetailContent = ({ hasPermissionToEdit, inspection, onInspectionMutated, isShare, }) => {
    const isPollingEnabled = shouldEnablePolling(inspection.property, isShare);
    const { useRemoteSubmissions } = useFeatureFlags();
    const { isPolling, data: pollData } = usePolling({
        interval: POLL_INTERVAL,
        maxAttempts: POLL_MAX_ATTEMPTS,
        firstRunDelay: POLL_INTERVAL,
        enabled: isPollingEnabled,
        query: (signal) => getOneProperty(inspection.property.id, { signal }),
        validator: (response) => {
            const property = unwrapModel(response);
            return !!property?.data_retrieval_attempted_at;
        },
    });
    const property = unwrapModel(pollData);
    const tokens = useTokens();
    const canEditInspection = hasPermissionToEdit && inspection.status.id !== CLOSED && !isShare;
    const inspectionWithUpdatedProperty = property && !inspection.property.data_retrieval_attempted_at
        ? set(lensProp('property'), mergeDeepLeft(property, inspection.property), inspection)
        : inspection;
    return (React.createElement(InspectionDetailProvider, { value: {
            canEditInspection,
            inspection: inspectionWithUpdatedProperty,
            isShare,
            onInspectionMutated,
            isPopulatingData: isPolling,
        } },
        React.createElement(PageHeader, { title: localise('INSPECTION'), isLoading: isPolling, loadingTooltip: localise('INSPECTION_DETAIL_POPULATING_TOOLTIP') },
            React.createElement(InspectionDetailHeader, { isShare: isShare, inspection: inspectionWithUpdatedProperty, canEditInspection: true })),
        React.createElement(PageContent, { pt: "34px" },
            React.createElement(HStack, { align: "start", w: "100%", spacing: tokens.Spacing08 },
                React.createElement(Box, { w: "70%" },
                    React.createElement(RenderIf, { condition: inspection.status.id === CLOSED && !isShare },
                        React.createElement(InspectionDetailCompletedAlert, { inspection: inspectionWithUpdatedProperty })),
                    React.createElement(RenderIf, { condition: !!property },
                        React.createElement(InspectionDetailDataPopulationAlert, null)),
                    React.createElement(VStack, { gap: tokens.Spacing06 },
                        React.createElement(InspectionDetailTabs, { inspection: inspectionWithUpdatedProperty }),
                        React.createElement(ContactsWrapper, { inspection: inspectionWithUpdatedProperty }))),
                React.createElement(Flex, { w: "30%", flexDirection: "column", gap: 4, mt: "36px" },
                    React.createElement(InspectionDetailDwellingDetailsAccordion, { property: inspectionWithUpdatedProperty.property }),
                    React.createElement(InspectionDetailPropertyOverviewAccordion, { inspection: inspectionWithUpdatedProperty }),
                    React.createElement(RenderIf, { condition: !!useRemoteSubmissions && !isShare },
                        React.createElement(TenantReportsCallToAction, { canEditInspection: canEditInspection, inspection: inspectionWithUpdatedProperty })))))));
};
export default InspectionDetailContent;
